/**
 * "Exclusive" only where it is true (SEO R3 rule 4, 2026-10-24). Scraped offer texts often say "exclusive" ("Apply this
 * exclusive Hostinger promo code", "Exclusive: 20% off") although the offer is not ours: on a coupon whose
 * `isExclusive` is false the word is removed from the public title and description. Pure, shared by the store page
 * (at render) and by the one-off database cleanup (`scripts/strip-unearned-exclusive.ts`).
 *
 * Only the adjective "exclusive" (and "exclusives") goes. "Exclusively" stays: "available exclusively to students" is a
 * restriction, not a claim that the offer is ours.
 */

const WORD = /\bexclusives?\b/i;

function articleFor(word: string): "a" | "an" {
    return /^[aeiou]/i.test(word) ? "an" : "a";
}

function matchCase(article: "a" | "an", original: string): string {
    return original[0] === original[0].toUpperCase() ? article[0].toUpperCase() + article.slice(1) : article;
}

/** The text without the word "exclusive"; null/empty stay as they are. */
export function stripExclusiveWording(text: string): string;
export function stripExclusiveWording(text: string | null | undefined): string | null | undefined;
export function stripExclusiveWording(text: string | null | undefined): string | null | undefined {
    if (!text || !WORD.test(text)) return text;
    const startsUpper = /^[^a-z]*[A-Z]/.test(text.trimStart());
    let s = text;
    // "(Exclusive)", "[Exclusive]"
    s = s.replace(/\s*[([]\s*exclusives?\s*[)\]]/gi, "");
    // "Affiliate Exclusive: ...", "Member Exclusive - ..." at the start: the qualifier goes with the word
    s = s.replace(/^\s*(affiliate|member|members|online|web|app|reader|subscriber|email|partner)\s+exclusives?\s*[:\-–—|]\s*/i, "");
    // "Exclusive: ...", "Exclusive - ...", "Exclusive | ..." at the start
    s = s.replace(/^\s*exclusives?\s*[:\-–—|]\s*/i, "");
    // "... - Exclusive" at the end
    s = s.replace(/\s*[:\-–—|]\s*exclusives?\s*[.!]?\s*$/i, "");
    // "an exclusive offer" -> "an offer" / "a deal": the article follows the next word
    s = s.replace(/\b(an?)\s+exclusives?\s+([A-Za-z0-9$€£%])/gi, (_m, art: string, next: string) => `${matchCase(articleFor(next), art)} ${next}`);
    // "exclusive 20% off", "this exclusive code"
    s = s.replace(/\bexclusives?\s+/gi, "");
    // anything left ("... CouponSwift exclusive.")
    s = s.replace(/\s*\bexclusives?\b/gi, "");
    s = s.replace(/\s{2,}/g, " ").replace(/\s+([.,;:!?])/g, "$1").trim();
    // A text that was nothing but the word ("Exclusive") still needs a heading.
    if (!s) return "Special offer";
    if (startsUpper) s = s[0].toUpperCase() + s.slice(1);
    return s;
}

/** The public text of an offer: unchanged when the offer is exclusive, otherwise without the word. */
export function publicOfferText(text: string | null | undefined, isExclusive: boolean | null | undefined): string | null | undefined {
    return isExclusive ? text : stripExclusiveWording(text);
}

/** True when the text says "exclusive" (the cleanup's candidate test). */
export function hasExclusiveWording(text: string | null | undefined): boolean {
    return !!text && WORD.test(text);
}
